import React, { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, Row, Col, Button, Input, Space, Modal, Tooltip, Steps, Transfer, Tag, Table, Typography, Alert, Collapse, Statistic } from "antd";
import Select from "react-select";
import _ from "lodash";
import EmailEditor, { EditorRef, EmailEditorProps } from "react-email-editor";
import {
  faAirFreshener,
  faAngleDoubleDown,
  faAngleDoubleUp,
  faAngleDown,
  faAngleUp,
  faArrowDown,
  faBackward,
  faBinoculars,
  faBookOpen,
  faBoxOpen,
  faEnvelope,
  faEnvelopeOpen,
  faEnvelopeSquare,
  faFolderOpen,
  faInbox,
  faMinus,
  faPaperPlane,
  faPlus,
  faSave,
  faUserSlash,
} from "@fortawesome/free-solid-svg-icons";

function TableTransfer(props) {
  const { leftColumns, rightColumns, ...restProps } = props;
  return (
    <Transfer
      titles={["Contact List", "Selected Contacts"]}
      style={{
        width: "100%",
      }}
      showSelectAll={false}
      {...restProps}
    >
      {({ direction, filteredItems, onItemSelect, onItemSelectAll, selectedKeys: listSelectedKeys, disabled: listDisabled }) => {
        const columns = direction === "left" ? leftColumns : rightColumns;
        const rowSelection = {
          getCheckboxProps: () => ({
            disabled: listDisabled,
          }),
          onChange(selectedRowKeys) {
            onItemSelectAll(selectedRowKeys, "replace");
          },
          selectedRowKeys: listSelectedKeys,
          selectionsIcon: <FontAwesomeIcon icon={faArrowDown} />,
          selections: [
            {
              key: "all",
              text: "Select All",
              onSelect: (changeableRowKeys) => {
                onItemSelectAll(
                  filteredItems.map((m) => m.client_contact_id),
                  "replace",
                );
              },
            },
            {
              key: "invert",
              text: "Invert Selection",
              onSelect: (changeableRowKeys) => {
                onItemSelectAll(
                  filteredItems.map((m) => m.client_contact_id).filter((f) => !listSelectedKeys.includes(f)),
                  "replace",
                );
              },
            },
            {
              key: "none",
              text: "Clear All Selections",
              onSelect: (changeableRowKeys) => {
                onItemSelectAll([], "replace");
              },
            },
          ],
        };
        return (
          <Table
            rowSelection={rowSelection}
            columns={columns}
            selections={true}
            dataSource={filteredItems}
            size="small"
            style={{
              pointerEvents: listDisabled ? "none" : undefined,
            }}
            onRow={({ key, disabled: itemDisabled }) => ({
              onClick: () => {
                if (itemDisabled || listDisabled) {
                  return;
                }
                onItemSelect(key, !listSelectedKeys.includes(key));
              },
            })}
          />
        );
      }}
    </Transfer>
  );
}

function EmailCampaignEditing(props) {
  const emailEditorRef = useRef();

  const { campaign_tag } = useParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [forceRerender, setForceRerender] = useState(0);
  const [emailCampaign, setEmailCampaign] = useState();
  const [authenticatedSenders, setAuthenticatedSenders] = useState([]);

  const [testEmails, setTestEmails] = useState(["", "", ""]);

  const [targetKeys, setTargetKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);
  const [contactWarnings, setContactWarnings] = useState({ no_emails: [], duplicate_emails: [] });

  const [metrics, setMetrics] = useState(null);
  const [complexMetricData, setComplexMetricData] = useState([]);

  const [openConfirmSendModal, setOpenConfirmSendModal] = useState(false);

  const [addAuthenticatedSenderProps, setAddAuthenticatedSenderProps] = useState({ open: false, email: "", show_next_steps: false });

  const onChange = (nextTargetKeys, direction, moveKeys) => {
    setEmailCampaign({ ...emailCampaign, target_audience: clientContacts.filter((f) => nextTargetKeys.includes(f.client_contact_id)), is_saved: false });
    setTargetKeys(nextTargetKeys);
  };
  const onSelectChange = (sourceSelectedKeys, targetSelectedKeys) => {
    setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys]);
  };

  const GetEmailCampaign = async () => {
    let response = await props.AxiosRequest("/api/email-campaigns/read", { tag: campaign_tag });
    setTargetKeys(response.data.email_campaign?.target_audience.map((m) => m.client_contacts_xref) || []);
    setEmailCampaign({ ...response.data.email_campaign, is_saved: true });
  };
  const GetEmailMetrics = async () => {
    let response = await props.AxiosRequest("/api/email-campaigns/read-metrics", { id: emailCampaign?.id });
    setMetrics(response.data.metrics);
    let reduction = response.data.metrics?.reduce((acc, curr) => {
      if (!acc[curr.message_id]) acc[curr.message_id] = []; //If this message_id wasn't previously stored
      acc[curr.message_id].push(curr);
      return acc;
    }, {});
    let complex_metrics = Object.keys(reduction);
    for (let i = 0; i < complex_metrics.length; i++) {
      complex_metrics[i] = {
        key: complex_metrics[i],
        message_id: complex_metrics[i],
        source: reduction[complex_metrics[i]][0].source,
        destination: reduction[complex_metrics[i]][0].destination,
        children: reduction[complex_metrics[i]],
      };
    }
    setComplexMetricData(complex_metrics);
  };

  const SaveEmailCampaign = async (suppress = false) => {
    if (emailCampaign?.status === "sent") return props.MakeNotification("warning", "This email campaign has already been sent, so you cannot modify it further.");
    if (contactWarnings?.no_emails.length)
      return props.MakeNotification("warning", "You cannot save this campaign with recipients that do not have email addresses. Please remove all recipients without email addresses before saving.");
    const unlayer = emailEditorRef.current?.editor;
    await unlayer?.exportHtml(async (data) => {
      const { design, html } = data;
      await props.AxiosRequest("/api/email-campaigns/update", { ...emailCampaign, email_content: html, email_template: design }, suppress);
      setEmailCampaign({ ...emailCampaign, is_saved: true });
    });
  };
  const GetAuthenticatedSenders = async () => {
    let response = await props.AxiosRequest("/api/authenticated-senders/get", { tag: campaign_tag });
    setAuthenticatedSenders(
      response.data.authenticated_senders.map((m) => {
        return { ...m, value: m.id, label: m.email };
      }),
    );
    return response.data;
  };
  const AddAuthenticatedSender = async () => {
    if (!addAuthenticatedSenderProps?.email) return props.MakeNotification("warning", "You must enter an email before authenticating it.");
    let response = await props.AxiosRequest("/api/authenticated-senders/insert", { email: addAuthenticatedSenderProps?.email });
    if (response?.data?.status === "success") setAddAuthenticatedSenderProps({ ...addAuthenticatedSenderProps, show_next_steps: true });
  };

  const SendTestEmails = async () => {
    if (!emailCampaign?.is_saved) return props.MakeNotification("warning", "Make sure to save your work before sending!");
    if (!testEmails[0]) return props.MakeNotification("warning", "You must enter an email before testing it.");
    await props.AxiosRequest("/api/email-campaigns/send-small-batch", { emails: testEmails, email_campaign_id: emailCampaign?.id });
  };
  const SendEmails = async () => {
    if (!emailCampaign?.is_saved) return props.MakeNotification("warning", "Make sure to save your work before sending!");
    let response = await props.AxiosRequest("/api/email-campaigns/send", { email_campaign_id: emailCampaign?.id });
    await GetEmailCampaign();
  };

  const GetClientContacts = async () => {
    let response = await props.AxiosRequest("/api/client-contacts/reporting/get/for-outreach");
    setClientContacts(
      response.data.client_contacts.map((m) => {
        return { ...m, key: m.client_contact_id };
      }),
    );
  };

  const onLoad = (unlayer) => {
    setForceRerender(forceRerender + 1);
  };
  const onReady = (unlayer) => {};

  useEffect(() => {
    GetEmailCampaign();
    GetAuthenticatedSenders();
    GetClientContacts();
  }, []);

  useEffect(() => {
    if (emailCampaign?.status === "sent") setCurrentStep(4);
  }, [emailCampaign?.status]);
  useEffect(() => {
    GetEmailMetrics();
  }, [emailCampaign?.id]);

  useEffect(() => {
    if (emailCampaign?.email_template && currentStep === 0) {
      // emailEditorRef.current.editor.loadDesign(JSON.parse(emailCampaign?.email_template));
      emailEditorRef?.current?.editor?.loadDesign(JSON.parse(emailCampaign?.email_template));
    }
  }, [emailCampaign?.email_template, forceRerender, currentStep]);

  const transfer_columns = [
    {
      dataIndex: "name",
      title: "Client Name",
    },
    {
      dataIndex: "client_categories",
      title: "Client Tag(s)",
      render: (value, row) => (value ? _.split(value, "| ").map((m, index) => <Tag key={"tag-key-" + row.id + "-" + index}>{m}</Tag>) : null),
    },
    {
      dataIndex: "contact_email",
      title: "Contact Email",
      render: (email) => (email ? email.toLowerCase() : null),
    },
    {
      dataIndex: "contact_name",
      title: "Contact Name",
    },
  ];
  const filterOption = (input, item) =>
    item.name?.toLowerCase().includes(input.toLowerCase()) ||
    item.contact_name?.toLowerCase().includes(input.toLowerCase()) ||
    item.client_categories?.toLowerCase().includes(input.toLowerCase()) ||
    item.contact_email?.toLowerCase().includes(input.toLowerCase());
  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      console.log(`selectedRowKeys: ${selectedRowKeys}`, "selectedRows: ", selectedRows);
    },
    onSelect: (record, selected, selectedRows) => {
      console.log(record, selected, selectedRows);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      console.log(selected, selectedRows, changeRows);
    },
  };
  const metric_columns = [
    {
      dataIndex: "destination",
      title: "Recipient",
    },
    {
      dataIndex: "source",
      title: "Source",
    },

    {
      dataIndex: "event_type",
      title: "Event",
      // render: (value, row) => (value ? _.split(value, "| ").map((m, index) => <Tag key={"tag-key-" + row.id + "-" + index}>{m}</Tag>) : null),
    },
    {
      dataIndex: "created_at",
      title: "Event Time",
      render: (value) => props.MakeDateTimeReadable(value, 2),
    },
  ];
  const steps = [
    {
      title: "Write and Preview Your Email",
      content: (
        <Row gutter={[8, 8]} style={{ display: currentStep === 0 ? "flex" : "none" }}>
          <Col span={11}>
            <Tooltip title="This is used internally only for you to name your campaigns, and won't be seen by any email recipients">
              <label className="col-form-label-sm">Email Campaign Name *</label>
            </Tooltip>
            <Input
              autoComplete="off"
              showCount
              maxLength={64}
              value={emailCampaign?.name}
              onChange={(e) => {
                setEmailCampaign({ ...emailCampaign, name: e?.target?.value, is_saved: false });
              }}
            />
          </Col>
          <Col span={11}>
            <label className="col-form-label-sm">Email Subject *</label>
            <Input
              autoComplete="off"
              showCount
              maxLength={128}
              value={emailCampaign?.email_subject}
              onChange={(e) => {
                setEmailCampaign({ ...emailCampaign, email_subject: e?.target?.value, is_saved: false });
              }}
            />
          </Col>
          <Col span={2}></Col>
          <Col span={7}>
            <label className="col-form-label-sm">Friendly 'From' Name </label>
            <Input
              maxLength={64}
              value={emailCampaign?.friendly_from_name}
              placeholder={'Example: "John Doe" or "AAA CPA\'s" '}
              onChange={(e) => {
                setEmailCampaign({ ...emailCampaign, friendly_from_name: e.target.value, is_saved: false });
              }}
            />
          </Col>
          <Col span={7}>
            <label className="col-form-label-sm">From Address *</label>
            <Select
              value={authenticatedSenders.find((f) => f.id === emailCampaign?.from_address_xref)}
              options={authenticatedSenders}
              noOptionsMessage={() => "Not seeing your email? Try adding the address with the button to the right."}
              onChange={(e) => {
                setEmailCampaign({ ...emailCampaign, from_address_xref: e.id, is_saved: false });
              }}
            />
          </Col>
          <Col span={7}>
            <label className="col-form-label-sm">Reply-To Address *</label>
            <Select
              value={authenticatedSenders.find((f) => f.id === emailCampaign?.reply_to_address_xref)}
              options={authenticatedSenders}
              noOptionsMessage={() => "Not seeing your email? Try adding the address with the button to the right."}
              onChange={(e) => {
                setEmailCampaign({ ...emailCampaign, reply_to_address_xref: e.id, is_saved: false });
              }}
            />
          </Col>
          <Col span={3}>
            <label className="col-form-label-sm w-100">&nbsp;</label>
            <Button className="w-100" onClick={(e) => setAddAuthenticatedSenderProps({ ...addAuthenticatedSenderProps, open: true, email: "" })}>
              Add Address
            </Button>
          </Col>
          <Col span={24}>
            <hr />
          </Col>
          <Col span={24}>
            <EmailEditor
              ref={emailEditorRef}
              onReady={onReady}
              onLoad={onLoad}
              options={{
                version: "latest",
                appearance: {
                  theme: "modern_light",
                },
                editor: { autoSelectOnDrop: true },
              }}
            />
          </Col>
        </Row>
      ),
    },
    {
      title: "Test Your Email",
      content: (
        <Row gutter={[8, 8]} style={{ display: currentStep === 1 ? "flex" : "none" }}>
          <Col span={16}>
            <Card>
              <label className="col-form-label-sm">Inbox Preview </label>
              <Input
                readOnly
                value={(emailCampaign?.friendly_from_name || authenticatedSenders?.find((f) => f.id === emailCampaign?.from_address_xref)?.label || "") + "\t\t\t" + emailCampaign?.email_subject}
                className="w-100 mb-2"
              />
              <label className="col-form-label-sm">Email Preview </label>
              <div dangerouslySetInnerHTML={{ __html: emailCampaign?.email_content }}></div>
            </Card>
          </Col>
          <Col span={8}>
            <Card title="">
              <Row gutter={[8, 8]}>
                <Col span={24} className="text-center">
                  <div>When you are ready to test your email out, you can send out test emails here. You can send emails to up to 3 people at a time.</div>
                </Col>

                <Col span={24}>
                  <label className="col-form-label-sm">Recipient #1 </label>
                  <Input
                    value={testEmails[0]}
                    onChange={(e) => {
                      setTestEmails(
                        testEmails.map((m, i) => {
                          if (i === 0) return e.target.value;
                          return m;
                        }),
                      );
                    }}
                    className="w-100 mb-2"
                  />
                </Col>
                {testEmails[0].length > 0 ? (
                  <Col span={24}>
                    <label className="col-form-label-sm">Recipient #2 </label>
                    <Input
                      value={testEmails[1]}
                      onChange={(e) => {
                        setTestEmails(
                          testEmails.map((m, i) => {
                            if (i === 1) return e.target.value;
                            return m;
                          }),
                        );
                      }}
                      className="w-100 mb-2"
                    />
                  </Col>
                ) : null}
                {testEmails[1].length > 0 ? (
                  <Col span={24}>
                    <label className="col-form-label-sm">Recipient #3 </label>
                    <Input
                      value={testEmails[2]}
                      onChange={(e) => {
                        setTestEmails(
                          testEmails.map((m, i) => {
                            if (i === 2) return e.target.value;
                            return m;
                          }),
                        );
                      }}
                      className="w-100 mb-2"
                    />
                  </Col>
                ) : null}
                <Col span={24} className="text-right">
                  {emailCampaign?.is_saved ? (
                    <Button onClick={SendTestEmails}>
                      <FontAwesomeIcon icon={faPaperPlane} className="mr-1" />
                      Send Test Emails
                    </Button>
                  ) : (
                    <Tooltip title="Please save your work before sending.">
                      <Button disabled>
                        <FontAwesomeIcon icon={faPaperPlane} className="mr-1" />
                        Send Test Emails
                      </Button>
                    </Tooltip>
                  )}
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      ),
    },
    {
      title: "Select Recipients",
      content: (
        <Row gutter={[8, 8]} style={{ display: currentStep === 2 ? "flex" : "none" }}>
          <Col span={24}>
            <Card>
              <Row gutter={[8, 8]}>
                {contactWarnings?.no_emails.length > 0 ? (
                  <Col span={24}>
                    <Collapse
                      expandIcon={() => null}
                      bordered={false}
                      defaultActiveKey={[]}
                      items={[
                        {
                          key: "warnings-1",
                          label: (
                            <Alert
                              message="Uh-Oh!"
                              description={
                                <>
                                  There are several contacts you selected that do <b>not</b> have an email address, so we can't send out emails to them. For the full list, please on this alert.
                                </>
                              }
                              type="error"
                              showIcon
                            ></Alert>
                          ),
                          children: (
                            <Row gutter={[8, 8]}>
                              <Col span={24} className="text-right">
                                <Button
                                  onClick={() => {
                                    let current_selection = clientContacts
                                      .filter((f) => targetKeys.includes(f.client_contact_id))
                                      .map((m) => {
                                        return { ...m, contact_email: m?.contact_email?.toLowerCase() };
                                      });
                                    //no email
                                    let no_emails = current_selection.filter((f) => !f?.contact_email || f?.contact_email.length === 0).map((m) => m.client_contact_id);
                                    onChange(targetKeys.filter((f) => !no_emails.includes(f)));
                                  }}
                                >
                                  Remove All Recipients That Don't Have Emails
                                </Button>
                              </Col>
                              <Col span={24}>
                                <Table
                                  pagination={false}
                                  columns={[
                                    {
                                      title: "Client Name",
                                      dataIndex: "name",
                                      key: "name",
                                      sorter: (a, b) => (a.name > b.name ? 1 : -1),
                                      defaultSortOrder: "ascend",
                                    },
                                    {
                                      title: "Contact Name",
                                      dataIndex: "contact_name",
                                      key: "contact_name",
                                    },
                                    {
                                      title: "Email",
                                      dataIndex: "contact_email",
                                      key: "contact_email",
                                      render: (value) => (value ? value : <Tag color="error">No Email Found</Tag>),
                                    },
                                  ]}
                                  dataSource={contactWarnings?.no_emails}
                                />
                              </Col>
                            </Row>
                          ),
                        },
                      ]}
                    />
                  </Col>
                ) : null}
                {contactWarnings?.duplicate_emails.length > 0 ? (
                  <Col span={24}>
                    <Collapse
                      expandIcon={() => null}
                      bordered={false}
                      defaultActiveKey={[]}
                      items={[
                        {
                          key: "warnings-2",
                          label: (
                            <Alert
                              message="Just Double Checking..."
                              description={
                                <>
                                  There are several contacts you selected that do have the same email addresses, so if you send it as is, they may receive the email multiple times. For the full list,
                                  please click this alert.
                                </>
                              }
                              type="warning"
                              showIcon
                            />
                          ),
                          children: (
                            <Row gutter={[8, 8]}>
                              <Col span={24}>
                                <Table
                                  pagination={false}
                                  columns={[
                                    {
                                      title: "Client Name",
                                      dataIndex: "name",
                                      key: "name",
                                      sorter: (a, b) => (a.name > b.name ? 1 : -1),
                                      defaultSortOrder: "ascend",
                                    },
                                    {
                                      title: "Contact Name",
                                      dataIndex: "contact_name",
                                      key: "contact_name",
                                    },
                                    {
                                      title: "Email",
                                      dataIndex: "contact_email",
                                      key: "contact_email",
                                      render: (value) => (value ? value : <Tag color="error">No Email Found</Tag>),
                                    },
                                  ]}
                                  dataSource={contactWarnings?.duplicate_emails}
                                />
                              </Col>
                            </Row>
                          ),
                        },
                      ]}
                    />
                  </Col>
                ) : null}
                <Card span={24}>
                  <TableTransfer
                    dataSource={clientContacts}
                    targetKeys={targetKeys}
                    disabled={false}
                    showSearch
                    onChange={onChange}
                    onSelectChange={onSelectChange}
                    filterOption={filterOption}
                    leftColumns={transfer_columns}
                    rightColumns={transfer_columns}
                    showSelectAll={false}
                  />
                </Card>
              </Row>
            </Card>
          </Col>
        </Row>
      ),
    },
    {
      title: "Send Your Email",
      content: (
        <Row gutter={[8, 8]} style={{ display: currentStep === 3 ? "flex" : "none" }}>
          <Col span={16}>
            <Row gutter={[8, 8]}>
              <Col span={24}>
                <Card title="Email Preview">
                  <label className="col-form-label-sm">Inbox Preview</label>
                  <Input
                    readOnly
                    value={(emailCampaign?.friendly_from_name || authenticatedSenders?.find((f) => f.id === emailCampaign?.from_address_xref)?.label || "") + "\t\t\t" + emailCampaign?.email_subject}
                    className="w-100 mb-2"
                  />
                  <label className="col-form-label-sm">Email Preview</label>
                  <div dangerouslySetInnerHTML={{ __html: emailCampaign?.email_content }}></div>
                </Card>
              </Col>
              <Col span={24}>
                <Card title="Recipient List">
                  <Table columns={transfer_columns} dataSource={clientContacts.filter((f) => targetKeys.includes(f.client_contact_id))} size="small" />
                </Card>
              </Col>
            </Row>
          </Col>
          <Col span={8}>
            <Card title="">
              <Row gutter={[8, 8]}>
                <Col span={24} className="text-center">
                  <div></div>
                </Col>

                <Col span={24} className="text-right">
                  {emailCampaign?.is_saved ? (
                    <Button
                      onClick={() => {
                        setOpenConfirmSendModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} className="mr-1" />
                      Send Emails
                    </Button>
                  ) : (
                    <Tooltip title="Please save your work before sending.">
                      <Button disabled>
                        <FontAwesomeIcon icon={faPaperPlane} className="mr-1" />
                        Send Emails
                      </Button>
                    </Tooltip>
                  )}
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      ),
    },
    {
      title: "Monitor Statistics",
      content: (
        <Row gutter={[8, 8]} style={{ display: currentStep === 4 ? "flex" : "none" }}>
          <Col span={24}>
            <Card>
              <Row gutter={[8, 8]}>
                <Col span={8}>
                  <Card title="Emails Sent" extra={<FontAwesomeIcon icon={faPaperPlane} />}>
                    <Row gutter={[8, 8]}>
                      <Col span={12}>
                        <Statistic title="Total Emails Sent" value={metrics?.filter((f) => f.event_type === "Send").length} />
                      </Col>
                      <Col span={12} className="text-right">
                        {/* <Statistic
                          title="Delivery Rate"
                          value={(metrics?.filter((f) => f.event_type === "Delivery").length / metrics?.filter((f) => f.event_type === "Initiated").length) * 100 + "%"}
                        /> */}
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card title="Emails Delivered" extra={<FontAwesomeIcon icon={faInbox} />}>
                    <Row gutter={[8, 8]}>
                      <Col span={12}>
                        <Statistic title="Total Emails Delivered" value={metrics?.filter((f) => f.event_type === "Delivery").length} />
                      </Col>
                      <Col span={12} className="text-right">
                        <Statistic
                          title="Delivery Rate"
                          value={
                            metrics?.filter((f) => f.event_type === "Initiated").length
                              ? props.FormatNumberTwoDecimals((metrics?.filter((f) => f.event_type === "Delivery").length / metrics?.filter((f) => f.event_type === "Initiated").length) * 100) + "%"
                              : "0%"
                          }
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card title="Emails Opened" extra={<FontAwesomeIcon icon={faBookOpen} />}>
                    <Row gutter={[8, 8]}>
                      <Col span={12}>
                        <Statistic title="Total Emails Opened" value={metrics?.filter((f) => f.event_type === "Delivery").length} />
                      </Col>
                      <Col span={12} className="text-right">
                        <Statistic
                          title="Open Rate"
                          value={
                            metrics?.filter((f) => f.event_type === "Initiated").length
                              ? props.FormatNumberTwoDecimals(
                                  (metrics
                                    ?.filter((f) => f.event_type === "Open")
                                    ?.reduce((acc, curr) => {
                                      if (!acc.find((f) => f.message_id === curr.message_id)) acc.push(curr);
                                      return acc;
                                    }, []).length /
                                    metrics?.filter((f) => f.event_type === "Initiated").length) *
                                    100,
                                ) + "%"
                              : "0%"
                          }
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col span={24} className="text-center">
                  <hr />
                </Col>
                <Col span={12}>
                  <Card title="Emails Bounced" extra={<FontAwesomeIcon icon={faBackward} />}>
                    <Row gutter={[8, 8]}>
                      <Col span={12}>
                        <Statistic
                          title="Total Emails Bounced"
                          value={
                            metrics?.filter((f) => f.event_type === "Bounce").length + metrics?.filter((f) => f.event_type === "HardBounce").length
                              ? metrics?.filter((f) => f.event_type === "Bounce").length + metrics?.filter((f) => f.event_type === "HardBounce").length
                              : 0
                          }
                        />
                      </Col>
                      <Col span={12} className="text-right">
                        <Statistic
                          title="Bounce Rate"
                          value={
                            metrics?.filter((f) => f.event_type === "Initiated").length
                              ? props.FormatNumberTwoDecimals(
                                  ((metrics?.filter((f) => f.event_type === "Bounce").length + metrics?.filter((f) => f.event_type === "HardBounce").length) /
                                    metrics?.filter((f) => f.event_type === "Initiated").length) *
                                    100,
                                ) + "%"
                              : "0%"
                          }
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Email Complaints" extra={<FontAwesomeIcon icon={faUserSlash} />}>
                    <Row gutter={[8, 8]}>
                      <Col span={12}>
                        <Statistic
                          title="Total Email Complaints"
                          value={metrics?.filter((f) => f.event_type === "Complaint").length ? metrics?.filter((f) => f.event_type === "Complaint").length : 0}
                        />
                      </Col>
                      <Col span={12} className="text-right">
                        <Statistic
                          title="Complaint Rate"
                          value={
                            metrics?.filter((f) => f.event_type === "Initiated").length
                              ? (metrics?.filter((f) => f.event_type === "Complaint").length / metrics?.filter((f) => f.event_type === "Initiated").length) * 100 + "%"
                              : "0%"
                          }
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col span={24} className="text-center">
                  <hr />
                </Col>
                <Col span={24} className="text-center">
                  <Typography.Title level={3}>Detailed View of Events</Typography.Title>
                  <Table
                    columns={metric_columns}
                    dataSource={complexMetricData}
                    size="small"
                    expandable={{
                      // expandedRowRender: record => (
                      //   <p style={{ margin: 0 }}>{record.description}</p>
                      // ),
                      expandIcon: ({ expanded, onExpand, record }) =>
                        record?.children ? (
                          expanded ? (
                            <Button size="small" className="mr-3" onClick={(e) => onExpand(record, e)}>
                              <FontAwesomeIcon icon={faAngleUp} />
                            </Button>
                          ) : (
                            <Button size="small" className="mr-3" onClick={(e) => onExpand(record, e)}>
                              <FontAwesomeIcon icon={faAngleDown} />
                            </Button>
                          )
                        ) : null,
                    }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      ),
    },
  ];

  useEffect(() => {
    //check for issues, such as...
    let current_selection = clientContacts
      .filter((f) => targetKeys.includes(f.client_contact_id))
      .map((m) => {
        return { ...m, contact_email: m?.contact_email?.toLowerCase() };
      });
    //no email
    let no_emails = current_selection.filter((f) => !f?.contact_email || f?.contact_email.length === 0);
    let duplicate_emails = current_selection
      .filter((value, i) => current_selection.filter((f) => f.contact_email === value?.contact_email).length > 1)
      .sort((a, b) => a.contact_email > b.contact_email);
    setContactWarnings({ no_emails, duplicate_emails });
    //duplicate emails
  }, [targetKeys]);

  return (
    <main className="content">
      <div className="container-fluid">
        <div className="header">
          <h1 className="header-title">Edit Email Campaign</h1>
        </div>
        <Row>
          <Card
            // size="small"
            title={emailCampaign?.name || "Email Campaign"}
            extra={
              <Button
                type="primary"
                onClick={() => {
                  SaveEmailCampaign();
                }}
              >
                <FontAwesomeIcon icon={faSave} className="mr-1" /> Save Campaign
              </Button>
            }
            style={{
              width: "100%",
            }}
          >
            <Steps type="navigation" size="small" current={currentStep} onChange={(e) => setCurrentStep(e)} className="site-navigation-steps mb-4" items={steps} />
            {/* <div>{steps[currentStep].content}</div> */}
            <div>{steps[0].content}</div>
            <div>{steps[1].content}</div>
            <div>{steps[2].content}</div>
            <div>{steps[3].content}</div>
            <div>{steps[4].content}</div>
          </Card>
        </Row>
      </div>
      <Modal
        open={addAuthenticatedSenderProps?.open}
        title="Add a New Authenticated Sender"
        okText="Send Authentication Email"
        onCancel={() => setAddAuthenticatedSenderProps({ open: false, email: "", show_next_steps: false })}
        onClose={() => setAddAuthenticatedSenderProps({ open: false, email: "", show_next_steps: false })}
        onOk={AddAuthenticatedSender}
      >
        <Row gutter={[8, 8]}>
          <Col span={24} className="mb-4">
            <label className="col-form-label-sm">New Email To Authenticate *</label>
            <Input
              value={addAuthenticatedSenderProps?.email}
              onChange={(e) => {
                setAddAuthenticatedSenderProps({ ...addAuthenticatedSenderProps, email: e.target.value });
              }}
            />
          </Col>
          {addAuthenticatedSenderProps?.show_next_steps ? (
            <Col span={24}>
              <Typography.Title level={4} className="text-center">
                Email Sent!
              </Typography.Title>
              <div className="text-center">
                Please check your email and follow the instructions in the email for the authenticated sender to show up. <br />
                <b>You may need to refresh this page after authenticating.</b>
              </div>
            </Col>
          ) : null}
        </Row>
      </Modal>
      {/*  */}
      <Modal
        open={openConfirmSendModal}
        title="Are you sure?"
        okText="Send Emails"
        width={800}
        onCancel={() => setOpenConfirmSendModal(false)}
        onClose={() => setOpenConfirmSendModal(false)}
        onOk={() => {
          SendEmails();
          setOpenConfirmSendModal(false);
        }}
      >
        <Row gutter={[8, 8]}>
          <Col span={24} className="text-center">
            <div>Are you sure you're ready to send these emails?</div>
            <div>Once you send this campaign out, you won't be able to edit this campaign anymore.</div>
          </Col>
        </Row>
      </Modal>
    </main>
  );
}

export default EmailCampaignEditing;
